import React, { useState } from "react";
import { Link } from "react-router-dom";
import styled from "styled-components";
import useScrollToTop from "../../blocks/logic/usScrollToTop";
import SEO from "../../components/SEO";
import ShopifyBuyButton from "../../components/ShopifyBuyButton";
import { Container, Section, Button } from "../../components/ui";

// Shopify product IDs for each pack size
const PRODUCT_IDS = {
  1: "8393084436792",
  3: "9259468980536",
  5: "8393098854712",
};

const PRICES = { 1: 25, 3: 60, 5: 95 };

const ProductSection = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 4rem;
  align-items: start;
  
  @media (max-width: 968px) {
    grid-template-columns: 1fr;
    gap: 2rem;
  }
`;

const ImageContainer = styled.div`
  position: sticky;
  top: 100px;
  
  @media (max-width: 968px) {
    position: static;
  }
`;

const MainImage = styled.div`
  width: 100%;
  aspect-ratio: 1;
  background-image: url(${({ $src }) => $src});
  background-size: cover;
  background-position: center;
  border-radius: 1rem;
  box-shadow: 0 20px 25px -5px rgb(0 0 0 / 0.1);
`;

const ProductInfo = styled.div``;

const Breadcrumb = styled.div`
  font-size: 0.875rem;
  color: #6b7280;
  margin-bottom: 1rem;
  
  a {
    color: #6b7280;
    &:hover { color: #0f0f0f; }
  }
`;

const ProductTitle = styled.h1`
  font-size: 2.5rem;
  margin-bottom: 1rem;
  
  @media (max-width: 768px) {
    font-size: 2rem;
  }
`;

const PriceTag = styled.div`
  font-size: 2rem;
  font-weight: 700;
  color: #eab308;
  margin-bottom: 1.5rem;
`;

const Description = styled.p`
  color: #6b7280;
  font-size: 1.125rem;
  line-height: 1.75;
  margin-bottom: 2rem;
`;

const OptionGroup = styled.div`
  margin-bottom: 1.5rem;
`;

const OptionLabel = styled.label`
  display: block;
  font-weight: 600;
  margin-bottom: 0.5rem;
  color: #374151;
`;

const QuantitySelect = styled.div`
  display: flex;
  gap: 0.75rem;
`;

const QuantityOption = styled.button`
  flex: 1;
  padding: 1rem;
  border: 2px solid ${({ $selected }) => $selected ? '#eab308' : '#e5e7eb'};
  border-radius: 0.5rem;
  background: ${({ $selected }) => $selected ? '#fef3c7' : 'white'};
  cursor: pointer;
  transition: all 150ms ease;
  
  &:hover {
    border-color: #eab308;
  }
`;

const QuantityNumber = styled.div`
  font-size: 1.5rem;
  font-weight: 700;
  color: #0f0f0f;
`;

const QuantityPrice = styled.div`
  font-size: 0.875rem;
  color: #6b7280;
`;

const BuySection = styled.div`
  margin-top: 2rem;
  padding-top: 2rem;
  border-top: 1px solid #e5e7eb;
`;

const Features = styled.div`
  margin-top: 2rem;
`;

const FeatureTitle = styled.h3`
  font-size: 1.25rem;
  margin-bottom: 1rem;
`;

const FeatureList = styled.ul`
  list-style: none;
  padding: 0;
`;

const FeatureItem = styled.li`
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
  margin-bottom: 0.75rem;
  color: #374151;
  
  &::before {
    content: "✓";
    color: #22c55e;
    font-weight: 700;
  }
`;

const DetailsSection = styled(Section)`
  background: #f9fafb;
`;

const DetailGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 2rem;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const DetailCard = styled.div`
  text-align: center;
  padding: 2rem;
  background: white;
  border-radius: 1rem;
`;

const DetailIcon = styled.div`
  font-size: 2rem;
  margin-bottom: 1rem;
`;

const DetailTitle = styled.h4`
  margin-bottom: 0.5rem;
`;

const DetailText = styled.p`
  color: #6b7280;
  font-size: 0.875rem;
  margin: 0;
`;

const ClassicProduct = () => {
  useScrollToTop();
  const [selectedAmount, setSelectedAmount] = useState(1);
  const displayPrice = PRICES[selectedAmount];
  const productId = PRODUCT_IDS[selectedAmount];

  return (
    <>
      <SEO
        title="The New Calendar Classic"
        description="The classic New Calendar and User Guide. A perpetual 5-season calendar that works from 2019-9999."
        url="https://thenewcalendar.com/newcalendar_classic"
        image="https://res.cloudinary.com/dqy3fktlv/image/upload/v1700665978/EKJClknXYAgIzI2_pk6dy9.jpg"
        type="product"
      />

      <Section>
        <Container>
          <ProductSection>
            <ImageContainer>
              <MainImage $src="https://res.cloudinary.com/dqy3fktlv/image/upload/v1700665978/EKJClknXYAgIzI2_pk6dy9.jpg" />
            </ImageContainer>

            <ProductInfo>
              <Breadcrumb>
                <Link to="/shop">Shop</Link> / Classic Calendar
              </Breadcrumb>

              <ProductTitle>The New Calendar Classic</ProductTitle>
              <PriceTag>${displayPrice}</PriceTag>

              <Description>
                The original perpetual wall calendar with comprehensive user guide. 
                Designed to last from 2019 to 9999—buy once, use forever.
              </Description>

              <OptionGroup>
                <OptionLabel>Select Quantity</OptionLabel>
                <QuantitySelect>
                  {[1, 3, 5].map(qty => (
                    <QuantityOption
                      key={qty}
                      $selected={selectedAmount === qty}
                      onClick={() => setSelectedAmount(qty)}
                    >
                      <QuantityNumber>{qty}</QuantityNumber>
                      <QuantityPrice>${PRICES[qty]}</QuantityPrice>
                    </QuantityOption>
                  ))}
                </QuantitySelect>
              </OptionGroup>

              <BuySection>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                  <ShopifyBuyButton 
                    productId={productId} 
                    buttonText="Add to Cart"
                    destination="cart"
                  />
                  <ShopifyBuyButton 
                    productId={productId} 
                    buttonText={`Buy Now - $${displayPrice}`}
                    destination="checkout"
                    showOptions={false}
                  />
                </div>
              </BuySection>

              <Features>
                <FeatureTitle>What's Included</FeatureTitle>
                <FeatureList>
                  <FeatureItem>Perpetual wall calendar (works 2019-9999)</FeatureItem>
                  <FeatureItem>Comprehensive user guide</FeatureItem>
                  <FeatureItem>Sunlight & temperature charts for 3 US latitudes</FeatureItem>
                  <FeatureItem>Seasonal tips and reminders</FeatureItem>
                  <FeatureItem>Free shipping (US)</FeatureItem>
                </FeatureList>
              </Features>
            </ProductInfo>
          </ProductSection>
        </Container>
      </Section>

      <DetailsSection>
        <Container>
          <h2 style={{ textAlign: 'center', marginBottom: '2rem' }}>Why Choose The Classic?</h2>
          <DetailGrid>
            <DetailCard>
              <DetailIcon>♻️</DetailIcon>
              <DetailTitle>Perpetual Design</DetailTitle>
              <DetailText>Days and dates never shift. Use the same calendar year after year.</DetailText>
            </DetailCard>
            <DetailCard>
              <DetailIcon>🇺🇸</DetailIcon>
              <DetailTitle>Made in USA</DetailTitle>
              <DetailText>Designed, engineered, and manufactured in the United States.</DetailText>
            </DetailCard>
            <DetailCard>
              <DetailIcon>📚</DetailIcon>
              <DetailTitle>User Guide Included</DetailTitle>
              <DetailText>Learn the science and logic behind the 5-season system.</DetailText>
            </DetailCard>
          </DetailGrid>
        </Container>
      </DetailsSection>
    </>
  );
};

export default ClassicProduct;
